// ControlDetailTab — the default tab showing control fields with inline editing.
// Row 1: Description. Row 2: Control definition (who/what/where/trigger/why).
// Row 3: Scope. Row 4: Classification & Ownership + Lifecycle transitions.
// Row 5: Summary counts.

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  controlsApi,
  controlLifecycleStateLabels,
  controlLifecycleStateColors,
  type UpdateControlRequest,
} from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Pencil, Check, X, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// --- Valid lifecycle transitions ---
const VALID_TRANSITIONS: Record<string, { target: string; label: string }[]> = {
  draft: [{ target: 'active', label: 'Activate' }],
  active: [{ target: 'retired', label: 'Retire' }],
  retired: [], // No transitions from retired
}

export function ControlDetailTab() {
  const { slug } = useTenant()
  const { controlId } = useParams({ strict: false }) as { controlId: string }
  const api = useApi()
  const ctrlApi = controlsApi(api)
  const queryClient = useQueryClient()

  const { data: control, isLoading } = useQuery({
    queryKey: queryKeys.controls.detail(slug, controlId),
    queryFn: () => ctrlApi.get(controlId),
  })

  // --- Editing state ---
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [draftOwner, setDraftOwner] = useState('')
  const [draftActivity, setDraftActivity] = useState('')
  const [draftLocation, setDraftLocation] = useState('')
  const [draftTrigger, setDraftTrigger] = useState('')
  const [draftRationale, setDraftRationale] = useState('')
  const [draftScopeValue, setDraftScopeValue] = useState('')
  const [draftScopeCoverage, setDraftScopeCoverage] = useState('')
  const [draftScopeExceptions, setDraftScopeExceptions] = useState('')

  // Sync draft state from server data
  useEffect(() => {
    if (control) {
      setDraftName(control.name)
      setDraftDescription(control.description ?? '')
      setDraftOwner(control.control_owner ?? '')
      setDraftActivity(control.control_activity ?? '')
      setDraftLocation(control.control_location ?? '')
      setDraftTrigger(control.control_trigger ?? '')
      setDraftRationale(control.control_rationale ?? '')
      setDraftScopeValue(control.scope_value ?? '')
      setDraftScopeCoverage(
        control.scope_coverage_percent != null ? String(control.scope_coverage_percent) : '',
      )
      setDraftScopeExceptions(control.scope_exceptions ?? '')
    }
  }, [control])

  const isDirty = useMemo(() => {
    if (!control) return false
    return (
      draftName !== control.name ||
      (draftDescription || '') !== (control.description ?? '') ||
      (draftOwner || '') !== (control.control_owner ?? '') ||
      (draftActivity || '') !== (control.control_activity ?? '') ||
      (draftLocation || '') !== (control.control_location ?? '') ||
      (draftTrigger || '') !== (control.control_trigger ?? '') ||
      (draftRationale || '') !== (control.control_rationale ?? '') ||
      (draftScopeValue || '') !== (control.scope_value ?? '') ||
      (draftScopeCoverage || '') !==
        (control.scope_coverage_percent != null ? String(control.scope_coverage_percent) : '') ||
      (draftScopeExceptions || '') !== (control.scope_exceptions ?? '')
    )
  }, [
    control,
    draftName,
    draftDescription,
    draftOwner,
    draftActivity,
    draftLocation,
    draftTrigger,
    draftRationale,
    draftScopeValue,
    draftScopeCoverage,
    draftScopeExceptions,
  ])

  const handleCancel = useCallback(() => {
    if (control) {
      setDraftName(control.name)
      setDraftDescription(control.description ?? '')
      setDraftOwner(control.control_owner ?? '')
      setDraftActivity(control.control_activity ?? '')
      setDraftLocation(control.control_location ?? '')
      setDraftTrigger(control.control_trigger ?? '')
      setDraftRationale(control.control_rationale ?? '')
      setDraftScopeValue(control.scope_value ?? '')
      setDraftScopeCoverage(
        control.scope_coverage_percent != null ? String(control.scope_coverage_percent) : '',
      )
      setDraftScopeExceptions(control.scope_exceptions ?? '')
    }
    setEditing(false)
  }, [control])

  // --- Change notes dialog state ---
  const [showNotesDialog, setShowNotesDialog] = useState(false)
  const [changeNotes, setChangeNotes] = useState('')
  const [pendingAction, setPendingAction] = useState<'update' | 'transition'>('update')
  const [pendingTransition, setPendingTransition] = useState<string | null>(null)

  // --- Update mutation ---
  const updateMutation = useMutation({
    mutationFn: (req: UpdateControlRequest) => ctrlApi.update(controlId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.detail(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.history(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.all(slug) })
      setShowNotesDialog(false)
      setChangeNotes('')
      setEditing(false)
    },
  })

  // --- Transition mutation ---
  const transitionMutation = useMutation({
    mutationFn: ({ target, notes }: { target: string; notes: string }) => {
      if (target === 'active') {
        return ctrlApi.activate(controlId)
      }
      return ctrlApi.retire(controlId, { change_notes: notes })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.detail(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.history(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.all(slug) })
      setShowNotesDialog(false)
      setChangeNotes('')
      setPendingTransition(null)
    },
  })

  const isMutating = updateMutation.isPending || transitionMutation.isPending

  const handleApply = useCallback(() => {
    setPendingAction('update')
    setShowNotesDialog(true)
  }, [])

  const handleTransition = useCallback((targetState: string) => {
    setPendingTransition(targetState)
    setPendingAction('transition')
    setShowNotesDialog(true)
  }, [])

  const handleConfirm = useCallback(() => {
    if (pendingAction === 'update' && control) {
      const req: UpdateControlRequest = {
        change_notes: changeNotes || 'Control updated',
      }
      if (draftName !== control.name) req.name = draftName
      if ((draftDescription || '') !== (control.description ?? ''))
        req.description = draftDescription || undefined
      if ((draftOwner || '') !== (control.control_owner ?? ''))
        req.control_owner = draftOwner || undefined
      if ((draftActivity || '') !== (control.control_activity ?? ''))
        req.control_activity = draftActivity || undefined
      if ((draftLocation || '') !== (control.control_location ?? ''))
        req.control_location = draftLocation || undefined
      if ((draftTrigger || '') !== (control.control_trigger ?? ''))
        req.control_trigger = draftTrigger || undefined
      if ((draftRationale || '') !== (control.control_rationale ?? ''))
        req.control_rationale = draftRationale || undefined
      if ((draftScopeValue || '') !== (control.scope_value ?? ''))
        req.scope_value = draftScopeValue || undefined
      const parsedCoverage = draftScopeCoverage ? parseInt(draftScopeCoverage, 10) : undefined
      if (
        parsedCoverage !== undefined &&
        parsedCoverage !== control.scope_coverage_percent
      ) {
        req.scope_coverage_percent = parsedCoverage
      }
      if ((draftScopeExceptions || '') !== (control.scope_exceptions ?? ''))
        req.scope_exceptions = draftScopeExceptions || undefined
      updateMutation.mutate(req)
    } else if (pendingAction === 'transition' && pendingTransition) {
      transitionMutation.mutate({
        target: pendingTransition,
        notes:
          changeNotes ||
          `State changed to ${controlLifecycleStateLabels[pendingTransition]}`,
      })
    }
  }, [
    pendingAction,
    pendingTransition,
    control,
    draftName,
    draftDescription,
    draftOwner,
    draftActivity,
    draftLocation,
    draftTrigger,
    draftRationale,
    draftScopeValue,
    draftScopeCoverage,
    draftScopeExceptions,
    changeNotes,
    updateMutation,
    transitionMutation,
  ])

  const handleCancelDialog = useCallback(() => {
    setShowNotesDialog(false)
    setChangeNotes('')
    setPendingTransition(null)
  }, [])

  if (isLoading || !control) return <FormSkeleton />

  const transitions = VALID_TRANSITIONS[control.lifecycle_state] ?? []

  return (
    <div className="space-y-6 p-6">
      {/* Edit toolbar */}
      <div className="flex items-center justify-end">
        {!editing ? (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit Details
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            {isDirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isMutating}>
              <X className="mr-1 h-3.5 w-3.5" /> Cancel
            </Button>
            <Button size="sm" onClick={handleApply} disabled={!isDirty || isMutating}>
              <Check className="mr-1 h-3.5 w-3.5" /> Apply Changes
            </Button>
          </div>
        )}
      </div>

      {/* Row 1: Description */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Description</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {updateMutation.isError && pendingAction === 'update' && (
            <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
              {(updateMutation.error as Error).message || 'Failed to save'}
            </div>
          )}
          <EditableField label="Name" value={draftName} onChange={setDraftName} editing={editing} />
          <EditableField
            label="Description"
            value={draftDescription}
            onChange={setDraftDescription}
            editing={editing}
            multiline
          />
        </CardContent>
      </Card>

      {/* Row 2: Control Definition (Who / What / Where / When / Why) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Control Definition</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <EditableField
              label="Who (Control Owner)"
              value={draftOwner}
              onChange={setDraftOwner}
              editing={editing}
            />
            <EditableField
              label="What (Activity)"
              value={draftActivity}
              onChange={setDraftActivity}
              editing={editing}
              multiline
            />
            <EditableField
              label="Where (Location)"
              value={draftLocation}
              onChange={setDraftLocation}
              editing={editing}
            />
            <EditableField
              label="When (Trigger)"
              value={draftTrigger}
              onChange={setDraftTrigger}
              editing={editing}
            />
          </div>
          <EditableField
            label="Why (Rationale)"
            value={draftRationale}
            onChange={setDraftRationale}
            editing={editing}
            multiline
          />
        </CardContent>
      </Card>

      {/* Row 3: Scope */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scope</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <EditableField
              label="Scope Value"
              value={draftScopeValue}
              onChange={setDraftScopeValue}
              editing={editing}
            />
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Coverage %
              </dt>
              {editing ? (
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                  value={draftScopeCoverage}
                  onChange={(e) => setDraftScopeCoverage(e.target.value)}
                >
                  <option value="">Not set</option>
                  {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((v) => (
                    <option key={v} value={String(v)}>
                      {v}%
                    </option>
                  ))}
                </select>
              ) : (
                <dd className="text-sm">
                  {control.scope_coverage_percent != null ? (
                    `${control.scope_coverage_percent}%`
                  ) : (
                    <span className="italic text-muted-foreground">Not set</span>
                  )}
                </dd>
              )}
            </div>
          </div>
          <EditableField
            label="Scope Exceptions"
            value={draftScopeExceptions}
            onChange={setDraftScopeExceptions}
            editing={editing}
            multiline
          />
        </CardContent>
      </Card>

      {/* Row 4: Classification & Lifecycle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Classification & Ownership</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <ReadField label="Category" value={control.category_name} />
            <ReadField label="Owner" value={control.owner_display_name} />
            <div>
              <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Lifecycle State
              </dt>
              <dd className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={cn('text-xs', controlLifecycleStateColors[control.lifecycle_state])}
                >
                  {controlLifecycleStateLabels[control.lifecycle_state]}
                </Badge>
                {transitions.map((t) => (
                  <Button
                    key={t.target}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={isMutating}
                    onClick={() => handleTransition(t.target)}
                  >
                    <ArrowRight className="mr-1 h-3 w-3" />
                    {t.label}
                  </Button>
                ))}
              </dd>
            </div>
            <ReadField label="Created Via" value={control.created_via} />
          </div>
        </CardContent>
      </Card>

      {/* Row 5: Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <SummaryItem label="Linked Risks" value={control.linked_risk_count} />
            <SummaryItem label="Linked Actions" value={control.linked_action_count} />
            <SummaryItem
              label="Overdue Assessments"
              value={control.has_overdue_assessments ? 'Yes' : 'No'}
              highlight={control.has_overdue_assessments}
            />
          </div>
        </CardContent>
      </Card>

      {/* Change notes dialog */}
      {showNotesDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-base">
                {pendingAction === 'transition'
                  ? `Confirm: ${controlLifecycleStateLabels[pendingTransition ?? ''] ?? pendingTransition}`
                  : 'Apply Changes'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="mb-1.5 block text-sm">
                  Change notes <span className="text-muted-foreground">(optional)</span>
                </Label>
                <textarea
                  className="min-h-[80px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Describe the reason for this change..."
                  value={changeNotes}
                  onChange={(e) => setChangeNotes(e.target.value)}
                  autoFocus
                />
              </div>
              {pendingAction === 'transition' && pendingTransition === 'retired' && (
                <div className="rounded bg-amber-50 p-2 text-sm text-amber-800">
                  Retiring a control is irreversible. The control will be permanently marked as
                  retired.
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={handleCancelDialog} disabled={isMutating}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleConfirm} disabled={isMutating}>
                  {isMutating ? 'Saving...' : 'Apply'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

// --- Shared helpers ---

function EditableField({
  label,
  value,
  onChange,
  editing,
  multiline,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  editing: boolean
  multiline?: boolean
}) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      {editing ? (
        multiline ? (
          <textarea
            className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <Input value={value} onChange={(e) => onChange(e.target.value)} />
        )
      ) : (
        <dd className="text-sm">
          {value || <span className="italic text-muted-foreground">Not set</span>}
        </dd>
      )}
    </div>
  )
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">
        {value || <span className="italic text-muted-foreground">Not set</span>}
      </dd>
    </div>
  )
}

function SummaryItem({
  label,
  value,
  highlight,
}: {
  label: string
  value: number | string
  highlight?: boolean
}) {
  return (
    <div className={cn('rounded p-3 text-center', highlight ? 'bg-amber-50' : 'bg-muted/30')}>
      <div className={cn('text-2xl font-semibold tabular-nums', highlight && 'text-amber-700')}>
        {value}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  )
}
